import React, { useCallback, useEffect, useRef, useState, forwardRef } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ChooseSeats from './components/ChooseSeats';
import { getBookingHubTimeSlots, getBookingHubSlotDetails, bookingHubSplitUp, createBookingHubOrder } from '../../api';
import '../../assets/css/bookingHub.css';
import '../../assets/css/eventPageStyle.css';
import SimpleReactValidator from 'simple-react-validator';
import Select from 'react-select';
import { COUNTRIES_LIST } from '../../appconstants';
import backArrow from '../../assets/imgs/backArrowBlack.svg';
import { notification } from 'antd';
import sorry from "../../assets/imgs/sorry.svg";
import formatAmountUtil from './utils/formatAmount';

// Convert minutes since midnight to "hh:mm AM/PM"
const minutesToTime = (minutes) => {
  if (minutes == null) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const ampm = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(hour12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
};

const BookingHubNext = ({ match }) => {
  const { gid, locationGid } = match.params;
  const history = useHistory();

  const [location, setLocation] = useState(null);
  const [showSupportPopup, setShowSupportPopup] = useState(false);
  const [bookingHub, setBookingHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [products, setProducts] = useState([]);

  // Date carousel — 14 days from today
  const today = moment();
  const dateOptions = Array.from({ length: 14 }, (_, i) => moment(today).add(i, 'days'));
  const [selectedDate, setSelectedDate] = useState(dateOptions[0]);


  // All timeslot data from API, keyed by date string
  const [allTimeSlots, setAllTimeSlots] = useState({}); // { 'YYYY-MM-DD': { scheduleGid, slots: [...] } }

  // Slots for selected date
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Ticket types
  const [ticketTypes, setTicketTypes] = useState([]);
  const [ticketTypesLoading, setTicketTypesLoading] = useState(false);

  // Events and Seating Chart state
  const [events, setEvents] = useState([]);
  const [showSeats, setShowSeats] = useState(false);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [activeEventIndex, setActiveEventIndex] = useState(0);

  // Quantities: { [ticketTypeGid]: number }
  const [quantities, setQuantities] = useState({});

  // Split-up totals
  const [subTotal, setSubTotal] = useState(0);
  const [serviceFee, setServiceFee] = useState(0);
  const [tax, setTax] = useState(0);
  const [total, setTotal] = useState(0);
  const [splitLoading, setSplitLoading] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isDescExpanded, setIsDescExpanded] = useState(false);

  const debounceTimerRef = useRef(null);
  const slotsContainerRef = useRef(null);
  // Tracks whether fetchSlotDetails is running for the first time (on mount/restore)
  const isMountingRef = useRef(true);
  // Tracks whether allTimeSlots has been loaded at least once (used in date-change guard)
  const allTimeSlotsLoadedRef = useRef(false);
  // Mirror of allTimeSlots for use inside effects without adding it as a dependency
  const allTimeSlotsRef = useRef({});
  // Set to true during session restore to prevent date-change effect from clearing session
  const sessionRestoredRef = useRef(false);

  // ── Countdown timer (only when slot has events) ──────────────
  const TIMER_DURATION = 600; // 10 minutes in seconds
  const [timerCount, setTimerCount] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const countdownRef = useRef(null);

  const [customInfoValues, setCustomInfoValues] = useState([]);
  const [countryCode, setCountryCode] = useState('+91');
  const [promoCode, setPromoCode] = useState('');
  const [selectedTip, setSelectedTip] = useState(null);
  const [customTipActive, setCustomTipActive] = useState(false);
  const [customTip, setCustomTip] = useState('');
  const [calculatedTip, setCalculatedTip] = useState(0);
  const simpleValidator = useRef(
    new SimpleReactValidator({
      element: (message) => <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>{message}</div>,
    })
  );

  const primaryColor = (location && location.colorCode) || '#C9822A';

  const scrollSlots = (direction) => {
    if (slotsContainerRef.current) {
      const cardWidth = slotsContainerRef.current.offsetWidth / 3;
      const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
      slotsContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const getTipAmount = () => {
    if (location?.acceptTip !== true) return 0;
    return calculatedTip;
  };

  const handleProceed = async () => {
    if (!canProceed || isCreatingOrder) return;
    setIsCreatingOrder(true);
    try {
      // Validate quantities before creating order
      for (const tt of ticketTypes) {
        const qty = quantities[tt.gid] || 0;
        if (qty > 0) {
          const available = tt.stockEnabled ? tt.currentStock : Infinity;
          const effectiveMin = (tt.stockEnabled && tt.currentStock < (tt.minQuantity || 0))
              ? 1
              : (tt.minQuantity || 0);

          if (qty < effectiveMin) {
            notification.error({
              message: 'Validation Error',
              description: `Please select at least ${effectiveMin} tickets for ${tt.name}.`,
              placement: 'topRight',
            });
            setIsCreatingOrder(false);
            return;
          }
          if (tt.maxQuantity > 0 && qty > tt.maxQuantity) {
            notification.error({
              message: 'Validation Error',
              description: `You can select a maximum of ${tt.maxQuantity} tickets for ${tt.name}.`,
              placement: 'topRight',
            });
            setIsCreatingOrder(false);
            return;
          }
          if (tt.stockEnabled && qty > tt.currentStock) {
            notification.error({
              message: 'Validation Error',
              description: `Only ${tt.currentStock} tickets are available for ${tt.name}.`,
              placement: 'topRight',
            });
            setIsCreatingOrder(false);
            return;
          }
        }
      }
      const items = [
        ...ticketTypes
          .filter(tt => (quantities[tt.gid] || 0) > 0)
          .map(tt => ({
            itemGid: tt.gid,
            quantity: quantities[tt.gid],
          })),
        ...selectedSeats.map(s => ({
          ticketGid: s.ticketTypeGid,
          quantity: 1,
          seats: [s.id],
          locationEventGid: s.eventGid,
          locationEventDate: selectedDate.format('YYYY-MM-DD'),
          locationEventStartTime: s.startTime,
          locationEventEndTime: s.endTime,
          holdToken: sessionStorage.getItem("seatsio")
            ? JSON.parse(sessionStorage.getItem("seatsio")).holdToken
            : null,
        }))
      ];

      const bookingHubOrderLabels = customInfoValues
        .filter(field => field.value && field.value.trim() !== '')
        .map(field => {
          let dataType = field.inputFieldType;
          if (field.inputFieldLabel.toLowerCase().includes("email")) dataType = "INPUT_EMAIL";
          if (field.inputFieldLabel.toLowerCase().includes("phone")) dataType = "INPUT_PHONE";
          if (field.inputFieldLabel.toLowerCase().includes("name") || field.inputFieldLabel.toLowerCase().includes("fullname")) dataType = "INPUT_NAME";
          
          let valueVal = field.value;
          if (dataType === "INPUT_PHONE") {
            const dialCode = countryCode || "+1";
            valueVal = valueVal.startsWith('+') ? valueVal : `${dialCode}${valueVal}`;
          }

          return {
            label: field.inputFieldLabel,
            value: valueVal,
            dataType: dataType || "INPUT_TEXT"
          };
        });

      const payload = {
        bookingHubLocationGid: locationGid,
        locationDate: selectedDate.format('YYYY-MM-DD'),
        locationStartTime: selectedSlot ? selectedSlot.startTime : null,
        locationEndTime: selectedSlot ? selectedSlot.endTime : null,
        currencyCode: (location && location.currency && location.currency.name) || 'USD',
        totalTip: getTipAmount(),
        bookingHubOrderLabels,
        bookingHubOrderLineItems: items,
        bookingHubOrderLink: {
          redirectUri: location.redirectUrl ? location.redirectUrl :
              (process.env.REACT_APP_ENV === "Production"
                  ? "https://dashboard.swirepay.com"
                  : "https://staging.swirepay.com") + "/booking-hub-receipts",
        },
      };

      if (customTipActive) {
        payload.tip = (Number(customTip) || 0) * 100;
        payload.tipType = 'AMOUNT';
      } else if (selectedTip) {
        payload.tip = selectedTip.tip;
        payload.tipType = selectedTip.tipType;
      }

      const orderEntity = await createBookingHubOrder(payload);
      if (orderEntity && orderEntity.bookingHubOrderLink && orderEntity.bookingHubOrderLink.link) {
        // Extract the path from the full link URL so it works on localhost too
        const linkUrl = new URL(orderEntity.bookingHubOrderLink.link);
        history.push(linkUrl.pathname);
      } else if (orderEntity && orderEntity.bookingHubOrderLink?.gid) {
        history.push(`/booking-hub-order-link/${orderEntity.bookingHubOrderLink.gid}/pay`);
      }
    } catch (err) {
      console.error('Error creating booking order:', err);
      const errMsg = err?.data?.message || err?.response?.data?.message
        || err?.response?.data?.exceptionReason
        || err?.message
        || 'Order creation failed. Please try again.';
      notification.destroy('bh-order-error');
      notification.error({
        key: 'bh-order-error',
        message: 'Order Failed',
        description: errMsg,
        duration: 5,
        placement: 'topRight',
      });
    } finally {
      setIsCreatingOrder(false);
    }
  };

  // ─── Fetch location info + all time slots on mount ──────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const entity = await getBookingHubTimeSlots(locationGid);
        // entity = { location: {...}, timeSlots: [{ gid, date, timeSlots: [...] }] }
        if (entity && entity.bookingHub) {
          setBookingHub(entity.bookingHub);
        }
        if (entity && entity.location) {
          const devAcceptTip = localStorage.getItem('dev_acceptTip');
          const devAcceptPromo = localStorage.getItem('dev_acceptPromo');
          if (devAcceptTip !== null) entity.location.acceptTip = devAcceptTip === 'true';
          if (devAcceptPromo !== null) entity.location.acceptPromo = devAcceptPromo === 'true';
          setLocation(entity.location);
        }
        // Build a map of date -> { scheduleGid, slots }
        if (entity && entity.timeSlots) {
          const tsMap = {};
          entity.timeSlots.forEach(entry => {
            const activeSlots = (entry.timeSlots || []).filter(ts => ts.available !== false).map(ts => ({
              ...ts,
              startTimeLabel: minutesToTime(ts.startTime),
              endTimeLabel: minutesToTime(ts.endTime),
            }));

            if (activeSlots.length > 0) {
              tsMap[entry.date] = {
                scheduleGid: entry.gid,
                slots: activeSlots,
              };
            }
          });
          setAllTimeSlots(tsMap);
          allTimeSlotsRef.current = tsMap;
          allTimeSlotsLoadedRef.current = true;

          // ── Try to restore session state (single key) ────────────────────
          const SESSION_KEY = `bh-booking-${locationGid}`;
          let dateRestored = false;
          try {
            const saved = sessionStorage.getItem(SESSION_KEY);
            if (saved) {
              const booking = JSON.parse(saved);
              // Validate: locationGid, dateStr and slotKey must all match
              if (
                booking.locationGid === locationGid &&
                booking.dateStr &&
                tsMap[booking.dateStr]
              ) {
                const matchedSlot = (tsMap[booking.dateStr].slots || []).find(s => {
                  const sk = s.hashValue || `${s.startTime}-${s.endTime}`;
                  return sk === booking.slotKey;
                });
                if (matchedSlot && booking.quantities && Object.keys(booking.quantities).length > 0) {
                  // Restore quantities into state BEFORE setting selectedSlot,
                  // so React has the correct value when the slot effect fires.
                  // Also set sessionRestoredRef so the date-change effect skips
                  // (it fires when setSelectedDate is called below).
                  sessionRestoredRef.current = true;
                  setQuantities(booking.quantities);
                  if (booking.selectedSeats && booking.selectedSeats.length > 0) {
                    setSelectedSeats(booking.selectedSeats);
                  }
                  setSelectedDate(moment(booking.dateStr));
                  setSlots(tsMap[booking.dateStr].slots);
                  setSelectedSlot(matchedSlot);
                  dateRestored = true;
                  // activeTab is restored in fetchSlotDetails after products load
                }
              }
            }
          } catch (e) {
            // Corrupted session — ignore, will fall through to auto-select
            sessionStorage.removeItem(`bh-booking-${locationGid}`);
          }

          if (!dateRestored) {
            // Auto-select the first date that has slots (prefer today if available)
            const todayStr = moment().format('YYYY-MM-DD');
            const availableDatesSorted = Object.keys(tsMap).sort();
            if (availableDatesSorted.length > 0) {
              const firstDate = availableDatesSorted.includes(todayStr)
                ? todayStr
                : availableDatesSorted[0];
              setSelectedDate(moment(firstDate));
              setSlots(tsMap[firstDate].slots);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching booking hub time slots', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [locationGid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location) {
      const fields = location.customInfos || location.eventPageCustomInfos || location.bookingHubCustomInfos || [
        { inputFieldLabel: "Full Name", inputFieldType: "INPUT_TEXT", isOptional: false, value: "" },
        { inputFieldLabel: "Email Address", inputFieldType: "INPUT_EMAIL", isOptional: false, value: "" },
        { inputFieldLabel: "Phone Number", inputFieldType: "INPUT_PHONE", isOptional: false, value: "" },
      ];
      setCustomInfoValues(fields.map(field => {
        let label = field.inputFieldLabel || field.label || '';
        if (label.toLowerCase() === "email") label = "Email Address";
        if (label.toLowerCase() === "phone") label = "Phone Number";
        return {
          ...field,
          inputFieldLabel: label,
          value: field.value || "",
        };
      }));
    }
  }, [location]);

  const handleInputChange = (index, val) => {
    setCustomInfoValues(prev => {
      const updated = [...prev];
      updated[index].value = val;
      return updated;
    });
  };

  const formatAddress = (addr) => {
    if (!addr) return null;
    if (typeof addr === 'string') return addr;
    const parts = [addr.street, addr.city, addr.state, addr.postalCode].filter(Boolean);
    return parts.join(', ');
  };

  // ─── Filter slots and auto-select first slot when date changes ───────────
  // NOTE: depends only on selectedDate to avoid firing when allTimeSlots loads on mount.
  // allTimeSlotsRef.current is used for slot lookup without adding it as a dep.
  useEffect(() => {
    // Skip on first mount (allTimeSlots not loaded yet, fetchData handles initial setup)
    if (!allTimeSlotsLoadedRef.current) return;
    // Skip when called as part of a session restore (setSelectedDate fires this)
    if (sessionRestoredRef.current) {
      sessionRestoredRef.current = false;
      return;
    }

    const dateStr = selectedDate.format('YYYY-MM-DD');
    const entry = allTimeSlotsRef.current[dateStr];

    // User changed date — clear the saved booking session
    sessionStorage.removeItem(`bh-booking-${locationGid}`);

    // Reset slot/ticket state
    setTicketTypes([]);
    setQuantities({});
    setSelectedSeats([]);
    setShowSeats(false);
    setSelectedTip(null);
    setCustomTipActive(false);
    setCustomTip('');
    resetTotals();

    if (!entry) {
      setSlots([]);
      setSelectedSlot(null);
      return;
    }

    // Show the time slot cards from API 1 data immediately
    setSlots(entry.slots);

    // Auto-select the first slot on this date
    if (entry.slots && entry.slots.length > 0) {
      setSelectedSlot(entry.slots[0]);
    } else {
      setSelectedSlot(null);
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Persist slot + date to sessionStorage (single booking key) ─────────
  // This is a lightweight save of slot identity only; quantities are saved directly
  // in handleQtyChange for reliability. This ensures the slot/date are always current.
  useEffect(() => {
    if (selectedSlot && !isMountingRef.current) {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const slotKey = selectedSlot.hashValue || `${selectedSlot.startTime}-${selectedSlot.endTime}`;
      const existing = (() => {
        try { return JSON.parse(sessionStorage.getItem(`bh-booking-${locationGid}`) || '{}'); } catch { return {}; }
      })();
      sessionStorage.setItem(`bh-booking-${locationGid}`, JSON.stringify({
        ...existing,
        locationGid,
        dateStr,
        slotKey,
      }));
    }
  }, [selectedSlot]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Persist activeTab to booking session key when it changes (after mount) ─────
  useEffect(() => {
    if (activeTab && !isMountingRef.current && selectedSlot) {
      const existing = (() => {
        try { return JSON.parse(sessionStorage.getItem(`bh-booking-${locationGid}`) || '{}'); } catch { return {}; }
      })();
      sessionStorage.setItem(`bh-booking-${locationGid}`, JSON.stringify({ ...existing, activeTab }));
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Persist selectedSeats to booking session key when they change (after mount) ────
  useEffect(() => {
    if (!isMountingRef.current && selectedSlot) {
      const existing = (() => {
        try { return JSON.parse(sessionStorage.getItem(`bh-booking-${locationGid}`) || '{}'); } catch { return {}; }
      })();
      sessionStorage.setItem(`bh-booking-${locationGid}`, JSON.stringify({ ...existing, selectedSeats }));
    }
  }, [selectedSeats]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Persist selected tip to sessionStorage when it changes ──────────────
  useEffect(() => {
    if (selectedTip) {
      sessionStorage.setItem(`bh-selected-tip-${locationGid}`, JSON.stringify(selectedTip));
    } else {
      sessionStorage.removeItem(`bh-selected-tip-${locationGid}`);
    }
  }, [selectedTip]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Persist custom tip active to sessionStorage when it changes ─────────
  useEffect(() => {
    sessionStorage.setItem(`bh-custom-tip-active-${locationGid}`, String(customTipActive));
  }, [customTipActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Persist custom tip input value to sessionStorage when it changes ────
  useEffect(() => {
    sessionStorage.setItem(`bh-custom-tip-${locationGid}`, customTip);
  }, [customTip]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Fetch slot details when selected slot changes ──────────────────────
  useEffect(() => {
    if (!selectedSlot) {
      setTicketTypes([]);
      return;
    }

    const fetchSlotDetails = async () => {
      setTicketTypesLoading(true);
      
      const isMounting = isMountingRef.current;
      // On mount: quantities were already restored by fetchData — skip reset.
      // On slot switch (!isMounting): read from single booking key (if same slot) or reset.
      if (!isMounting) {
        let nextQuantities = {};
        let nextSeats = [];
        try {
          const saved = sessionStorage.getItem(`bh-booking-${locationGid}`);
          if (saved) {
            const booking = JSON.parse(saved);
            const slotKey = selectedSlot.hashValue || `${selectedSlot.startTime}-${selectedSlot.endTime}`;
            if (booking.slotKey === slotKey && booking.quantities) {
              nextQuantities = booking.quantities;
            }
          }
        } catch (e) { /* ignore */ }

        setQuantities(nextQuantities);
        setSelectedSeats(nextSeats);
        setShowSeats(false);
        resetTotals();
      }
      try {
        const dateStr = selectedDate.format('YYYY-MM-DD');
        const entry = allTimeSlots[dateStr];
        const scheduleGid = entry ? entry.scheduleGid : null;
        if (!scheduleGid) {
          setTicketTypes([]);
          return;
        }

        const entity = await getBookingHubSlotDetails(scheduleGid);
        
        // Find the matching time slot entry by hashValue or startTime+endTime
        const targetTs = (entity.timeSlots || []).find(
          ts => ts.hashValue === selectedSlot.hashValue || (ts.startTime === selectedSlot.startTime && ts.endTime === selectedSlot.endTime)
        );

        const slotEvents = targetTs ? (targetTs.events || []) : [];
        setEvents(slotEvents);
        
        setActiveEventIndex(0);
        if (!isMounting) {
          setShowSeats(false);
        }

        const items = [];
        const rawProducts = targetTs ? targetTs.products : [];
        for (const product of rawProducts) {
          for (const item of (product.items || [])) {
            if (item.isAvailable === false) continue;
            items.push({
              gid: item.gid,
              name: item.name,
              amount: item.amount,
              stockEnabled: item.stockEnabled,
              currentStock: item.currentStock,
              minQuantity: item.minQuantity,
              maxQuantity: item.maxQuantity,
              isAvailable: item.isAvailable !== false,
              productType: product.productType || 'TICKETS',
            });
          }
        }
        setTicketTypes(items);
        setProducts(rawProducts);
        if (rawProducts.length > 0) {
          // On mount/restore: use saved activeTab if valid, otherwise default to first product
          const savedBooking = (() => {
            try { return JSON.parse(sessionStorage.getItem(`bh-booking-${locationGid}`) || '{}'); } catch { return {}; }
          })();
          const savedTab = isMounting && savedBooking.activeTab;
          const validSavedTab = savedTab && rawProducts.some(p => p.gid === savedTab);
          setActiveTab(validSavedTab ? savedTab : rawProducts[0].gid);
        } else {
          setActiveTab('');
        }
      } catch (err) {
        console.error('Error fetching slot details', err);
        setTicketTypes([]);
      } finally {
        setTicketTypesLoading(false);
        isMountingRef.current = false;
      }
    };

    fetchSlotDetails();
  }, [selectedSlot]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Start / stop timer based on whether slot has events ───────
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setTimerActive(false);
    setTimerCount(0);

    if (events.length > 0 && selectedSlot) {
      // Start a fresh 10-minute countdown
      setTimerCount(TIMER_DURATION);
      setTimerActive(true);
      countdownRef.current = setInterval(() => {
        setTimerCount(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            setTimerActive(false);
            // Timer expired — reset all seat selections and notify user
            setQuantities({});
            setSelectedSeats([]);
            setSelectedSlot(null);
            setSelectedTip(null);
            setCustomTipActive(false);
            setCustomTip('');
            resetTotals();
            // Clear all session state on timer expire (including seatsio holdToken)
            Object.keys(sessionStorage).forEach((key) => {
              if (key.startsWith('bh-') || key === 'seatsio') {
                sessionStorage.removeItem(key);
              }
            });
            notification.warning({
              key: 'bh-timer-expired',
              message: 'Session Expired',
              description: 'Your seat hold time has expired. Please select a slot again.',
              duration: 6,
              placement: 'topRight',
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [events, selectedSlot]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Recalculate total on quantity change (debounced) ────────────────────
  const recalculate = useCallback(async (newQuantities, newSelectedSeats) => {
    const items = [
      ...ticketTypes
        .filter(tt => (newQuantities[tt.gid] || 0) > 0)
        .map(tt => ({
          itemGid: tt.gid,
          quantity: newQuantities[tt.gid],
        })),
        ...newSelectedSeats.map(s => ({
          ticketGid: s.ticketTypeGid,
          quantity: 1,
          seats: [s.id],
          locationEventGid: s.eventGid,
          locationEventDate: selectedDate.format('YYYY-MM-DD'),
          locationEventStartTime: s.startTime,
          locationEventEndTime: s.endTime,
          holdToken: sessionStorage.getItem("seatsio")
            ? JSON.parse(sessionStorage.getItem("seatsio")).holdToken
            : null,
        }))
    ];

    if (items.length === 0) {
      resetTotals();
      return;
    }

    setSplitLoading(true);
    try {
      const payload = {
        bookingHubLocationGid: locationGid,
        locationDate: selectedDate.format('YYYY-MM-DD'),
        locationStartTime: selectedSlot ? selectedSlot.startTime : null,
        locationEndTime: selectedSlot ? selectedSlot.endTime : null,
        bookingHubOrderLineItems: items,
      };

      if (customTipActive) {
        payload.tip = (Number(customTip) || 0) * 100;
        payload.tipType = 'AMOUNT';
      } else if (selectedTip) {
        payload.tip = selectedTip.tip;
        payload.tipType = selectedTip.tipType;
      }

      const result = await bookingHubSplitUp(payload);
      setSubTotal(result.subTotal || 0);
      setServiceFee(result.serviceFee || 0);
      setTax(result.tax || 0);
      setCalculatedTip(result.tipAmount || 0);
      setTotal(result.netPayable || result.subTotal || 0);
    } catch (err) {
      // fail silently
    } finally {
      setSplitLoading(false);
    }
  }, [ticketTypes, selectedSlot, selectedDate, selectedTip, customTipActive, customTip, locationGid]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      recalculate(quantities, selectedSeats);
    }, 300);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [selectedTip, customTipActive, customTip, selectedDate, selectedSlot, quantities, selectedSeats, recalculate]);

  const handleQtyChange = (ticketGid, delta) => {
    const item = ticketTypes.find(tt => tt.gid === ticketGid);
    if (!item) return;

    const available = item.stockEnabled ? (item.currentStock ?? Infinity) : Infinity;
    const effectiveMin = (item.stockEnabled && item.currentStock < (item.minQuantity || 0))
        ? 1
        : (item.minQuantity || 0);

    setQuantities(prev => {
      const currentQty = prev[ticketGid] || 0;
      let newQty = currentQty;

      if (delta > 0) {
        if (currentQty === 0) {
          newQty = effectiveMin > 0 ? Math.min(effectiveMin, available) : 1;
        } else {
          const nextQty = currentQty + delta;
          if (item.maxQuantity && nextQty > item.maxQuantity) {
            newQty = currentQty;
          } else if (item.stockEnabled && nextQty > item.currentStock) {
            newQty = currentQty;
          } else {
            newQty = nextQty;
          }
        }
      } else if (delta < 0) {
        const nextQty = currentQty + delta;
        if (effectiveMin > 0 && currentQty <= effectiveMin) {
          newQty = 0;
        } else if (nextQty <= 0) {
          newQty = 0;
        } else {
          newQty = nextQty;
        }
      }

      const nextQuantities = Object.assign({}, prev, { [ticketGid]: newQty });
      // Write directly to the single booking session key so refresh restores correctly.
      if (selectedSlot) {
        const dateStr = selectedDate.format('YYYY-MM-DD');
        const slotKey = selectedSlot.hashValue || `${selectedSlot.startTime}-${selectedSlot.endTime}`;
        // Read existing to preserve selectedSeats and activeTab already stored
        const existing = (() => {
          try { return JSON.parse(sessionStorage.getItem(`bh-booking-${locationGid}`) || '{}'); } catch { return {}; }
        })();
        sessionStorage.setItem(`bh-booking-${locationGid}`, JSON.stringify({
          ...existing,
          locationGid,
          dateStr,
          slotKey,
          quantities: nextQuantities,
        }));
      }
      return nextQuantities;
    });
  };

  const resetTotals = () => {
    setSubTotal(0);
    setServiceFee(0);
    setTax(0);
    setCalculatedTip(0);
    setTotal(0);
  };

  const handleSeatSelected = useCallback((object, matchedEventSlot) => {
    const activeEvent = events[activeEventIndex];
    if (!activeEvent || !matchedEventSlot) return;

    let ticketTypeGid = object.selectedTicketType;
    let ticketTypeName = object.category?.label || 'Seat';
    let price = 0;

    try {
      if (matchedEventSlot.seatData) {
        const parsed = JSON.parse(atob(matchedEventSlot.seatData));
        const pricingCat = parsed?.pricingData?.find(p => String(p.category) === String(object.category?.key));
        if (pricingCat) {
          const tt = pricingCat.ticketTypes?.find(t => t.ticketType === ticketTypeGid) || pricingCat.ticketTypes?.[0];
          if (tt) {
            ticketTypeGid = tt.ticketType;
            ticketTypeName = tt.label;
            price = tt.price;
          }
        }
      }
    } catch (err) {
      console.error("Error matching ticket type:", err);
    }

    if (!ticketTypeGid) {
      ticketTypeGid = object.category?.key || object.id;
    }

    const newSeat = {
      id: object.id,
      label: object.label || object.id,
      categoryKey: object.category?.key,
      ticketTypeGid,
      ticketTypeName,
      price: price * 100,
      eventGid: activeEvent.gid,
      eventName: activeEvent.name,
      startTime: matchedEventSlot.startTime,
      endTime: matchedEventSlot.endTime
    };

    setSelectedSeats(prev => {
      if (prev.some(s => s.id === newSeat.id && s.eventGid === newSeat.eventGid)) {
        return prev;
      }
      const updated = [...prev, newSeat];
      recalculate(quantities, updated);
      return updated;
    });
  }, [events, activeEventIndex, quantities, recalculate]);

  const handleSeatDeselected = useCallback((object) => {
    const activeEvent = events[activeEventIndex];
    if (!activeEvent) return;

    setSelectedSeats(prev => {
      const updated = prev.filter(s => !(s.id === object.id && s.eventGid === activeEvent.gid));
      recalculate(quantities, updated);
      return updated;
    });
  }, [events, activeEventIndex, quantities, recalculate]);

  const normCurrency = location?.currency ? {
    ...location.currency,
    countryAlpha2: location.currency.countryAlpha || location.currency.countryAlpha2 || 'US'
  } : { prefix: '\u20B9', name: 'INR', toFixed: '2', countryAlpha2: 'IN' };

  const currencySymbol = normCurrency.prefix || '\u20B9';

  const formatAmount = (amountInPaise) => {
    if (amountInPaise === 0) return 'Free';
    return currencySymbol + formatAmountUtil(amountInPaise / 100, normCurrency);
  };

  const formatAmountNum = (amountInPaise) => currencySymbol + formatAmountUtil(amountInPaise / 100, normCurrency);

  const totalTickets = Object.values(quantities).reduce((s, q) => s + q, 0);
  const grandTotal = total;

  const isCustomInfoValid = () => {
    return customInfoValues.every(field => {
      const isRequired = field.isOptional === false;
      if (!isRequired) return true;
      return field.value && field.value.trim() !== '';
    });
  };

  const canProceed = showSeats
    ? (selectedSlot && selectedSeats.length > 0 && total > 0 && isCustomInfoValid())
    : (events.length > 0
        ? (selectedSlot && isCustomInfoValid())
        : (selectedSlot && totalTickets > 0 && total > 0 && isCustomInfoValid())
      );

  const supportContact = location?.supports?.find(s => s.isPrimary) || location?.supports?.[0] || location?.support;
  const supportEmail = supportContact?.email || location?.supportEmail;
  const supportPhone = supportContact?.phoneNumber || supportContact?.phone || location?.supportPhone;

  if (loading) {
    return (
      <div className="bh-next-page">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '60vh' }}>
          <div className="bh-next-spinner" style={{ width: 36, height: 36, borderTopColor: '#C9822A' }} />
        </div>
      </div>
    );
  }

  if (location && location.active === false) {
    return (
      <div 
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#F9FAFB',
          padding: '24px',
          fontFamily: 'Inter, sans-serif'
        }}
      >
        <div 
          style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '40px 32px',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.05)',
            textAlign: 'center',
            maxWidth: '440px',
            width: '100%',
            border: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px'
          }}
        >
          <img 
            src={sorry} 
            alt="Sorry" 
            style={{
              width: '160px',
              height: 'auto',
              display: 'block'
            }} 
          />
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', margin: 0 }}>
            Booking Temporarily Closed
          </h2>
          <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.6', margin: 0 }}>
            Sorry, bookings for this booking portal are currently closed. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bh-next-page">

      {/* Top Banner Header */}
      {bookingHub && (
        <div 
          className="bh-next-top-banner" 
          style={{ 
            backgroundColor: primaryColor,
            color: '#fff',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
          }}
        >
          {/* Left Side: Logo */}
          <div className="bh-next-banner-left">
            {bookingHub.logo ? (
              <img 
                src={bookingHub.logo} 
                alt="Logo" 
                style={{ 
                  maxHeight: '38px', 
                  objectFit: 'contain',
                  display: 'block'
                }} 
              />
            ) : (
              <div 
                style={{ 
                  width: '38px', 
                  height: '38px', 
                  borderRadius: '6px', 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '16px'
                }}
              >
                {(bookingHub.header || 'H').charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* Center: Title & Subtitle */}
          <div className="bh-next-banner-center">
            <div style={{ fontWeight: '700', fontSize: '14px', lineHeight: '1.2' }}>
              {bookingHub.header || 'Banner Text'}
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.75)', marginTop: '2px' }}>
              {bookingHub.subHeader || 'Event Management Platform'}
            </div>
          </div>

          {/* Right Side: Contact Support Button */}
          {(() => {
            if (!supportEmail && !supportPhone) return null;

            return (
              <div className="bh-next-banner-right" style={{ display: 'flex', alignItems: 'center' }}>
                <button 
                  onClick={() => setShowSupportPopup(true)}
                  className="bh-next-banner-support-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    borderRadius: '16px',
                    padding: '6px 12px',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    outline: 'none',
                    borderStyle: 'solid'
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  <span>Contact Support</span>
                </button>
              </div>
            );
          })()}
        </div>
      )}

      {/* Navigation Row */}
      <div className="bh-next-nav-row">
        <div className="bh-next-nav-row-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button 
              onClick={() => history.goBack()}
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '50%',
                width: '38px',
                height: '38px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                padding: 0,
                flexShrink: 0
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
            >
              <img src={backArrow} alt="Back" style={{ width: '20px', height: '20px', display: 'block' }} />
            </button>
            <h1 className="bh-next-page-heading">Select Your Tickets</h1>
          </div>

          {/* Countdown timer — only shown when slot has seated events */}
          {timerActive && timerCount > 0 && (
            <div
              className="modern-pill-timer"
              style={{ position: 'relative', top: 'auto', right: 'auto', transform: 'none', marginLeft: 'auto', zIndex: 10 }}
              title="Your seat hold expires when the timer reaches 0"
            >
              <span className="modern-pill-timer-label" style={{ fontSize: '11px' }}>Time Left</span>
              <span
                className="modern-pill-timer-time"
                style={{
                  fontSize: '18px',
                  minWidth: '52px',
                  color: timerCount > 360 ? '#2db300' : timerCount > 120 ? '#e67300' : '#cc0000',
                  textShadow: 'none',
                }}
              >
                {`${Math.floor(timerCount / 60)}:${String(timerCount % 60).padStart(2, '0')}`}
              </span>
              <div className="modern-pill-timer-icon" style={{ width: '20px', height: '20px', borderColor: timerCount > 360 ? '#2db300' : timerCount > 120 ? '#e67300' : '#cc0000' }}>
                <div className="modern-pill-timer-dot" style={{ transformOrigin: 'center 5px', background: timerCount > 360 ? '#2db300' : timerCount > 120 ? '#e67300' : '#cc0000' }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Body: two-column layout */}
      <div className="bh-next-body">

        {/* ── LEFT COLUMN ── */}
        <div className="bh-next-main">
          {showSeats ? (
            <ChooseSeats
              events={events}
              activeEventIndex={activeEventIndex}
              setActiveEventIndex={setActiveEventIndex}
              onSeatSelected={handleSeatSelected}
              onSeatDeselected={handleSeatDeselected}
              primaryColor={primaryColor}
              setShowSeats={setShowSeats}
              selectedSlot={selectedSlot}
            />
          ) : (
            <>
              {/* Location Info + Date + Slots */}
              <div className="bh-next-section bh-next-location-card">
                <h2 className="bh-next-location-name">
                  {location?.landmark && `${location.landmark.toUpperCase()} - `}{location?.name?.toUpperCase()}
                </h2>
                {location?.description && (
                  <p className="bh-next-location-description">
                    {isDescExpanded || location.description.length <= 180
                      ? location.description
                      : `${location.description.slice(0, 180)}...`}
                    {location.description.length > 180 && (
                      <span
                        style={{ color: primaryColor, fontWeight: 'bold', cursor: 'pointer', marginLeft: '6px' }}
                        onClick={() => setIsDescExpanded(!isDescExpanded)}
                      >
                        {isDescExpanded ? 'Read Less' : 'Read More'}
                      </span>
                    )}
                  </p>
                )}

                {/* Date picker - custom styled using react-datepicker */}
                {(() => {
                  const availableDates = Object.keys(allTimeSlots).map(dateStr => moment(dateStr).toDate());
                  
                  const CustomDatePickerInput = forwardRef(({ value, onClick }, ref) => (
                    <div 
                      className="bh-next-datepicker-field" 
                      onClick={onClick} 
                      ref={ref}
                      style={{ position: 'relative' }}
                    >
                      <span className="bh-next-datepicker-display">
                        {value}
                      </span>
                      <span className="bh-next-datepicker-icon">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="2" y="3" width="14" height="13" rx="2" stroke="#6A7282" strokeWidth="1.5"/>
                          <path d="M2 8H16M6 2V4M12 2V4" stroke="#6A7282" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </span>
                    </div>
                  ));

                  return (
                    <div className="bh-next-datepicker-row">
                      <span className="bh-next-datepicker-label" style={{ userSelect: 'none' }}>Select booking Date:</span>
                      <DatePicker
                        selected={selectedDate.toDate()}
                        onChange={(date) => {
                          if (date) {
                            setSelectedDate(moment(date));
                          }
                        }}
                        includeDates={availableDates}
                        customInput={<CustomDatePickerInput />}
                        dateFormat="dd MMM yyyy"
                        wrapperClassName="bh-next-datepicker-wrapper"
                      />
                    </div>
                  );
                })()}

                {/* Slots */}
                {slotsLoading ? (
                  <div className="bh-next-loading-row" style={{ marginTop: 16 }}>
                    <div className="bh-next-spinner" style={{ borderTopColor: primaryColor }} />
                    <span>Loading available slots&hellip;</span>
                  </div>
                ) : slots.length === 0 ? (
                  <p className="bh-next-empty" style={{ marginTop: 16 }}>No slots available for this date.</p>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '8px', marginTop: 16, position: 'relative' }}>
                    {slots.length > 3 && (
                      <button 
                        onClick={() => scrollSlots('left')}
                        className="bh-next-slots-scroll-btn"
                        style={{
                          border: '1.5px solid #D1D5DC',
                          background: '#fff',
                          borderRadius: '50%',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          flexShrink: 0,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          padding: 0
                        }}
                      >
                        <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
                          <path d="M5 9L1 5L5 1" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}

                    <div 
                      ref={slotsContainerRef}
                      className="bh-next-slot-cards-row bh-next-slots-scroll-container"
                      style={{ 
                        flex: 1, 
                        display: 'flex', 
                        gap: '10px', 
                        overflowX: 'auto',
                        flexWrap: 'nowrap',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                      }}
                    >
                      {slots.map(slot => {
                        const isActive = selectedSlot && (selectedSlot.hashValue === slot.hashValue || (selectedSlot.startTime === slot.startTime && selectedSlot.endTime === slot.endTime));
                        return (
                          <button
                            key={slot.hashValue || `${slot.startTime}-${slot.endTime}`}
                            className={'bh-next-slot-card' + (isActive ? ' active' : '')}
                            style={isActive
                              ? { borderColor: primaryColor, background: `${primaryColor}12` }
                              : { borderColor: '#D1D5DC' }
                            }
                            onClick={() => {
                              const slotKey = slot.hashValue || `${slot.startTime}-${slot.endTime}`;
                              let nextQuantities = {};
                              let nextSeats = [];
                              try {
                                const saved = sessionStorage.getItem(`bh-booking-${locationGid}`);
                                if (saved) {
                                  const booking = JSON.parse(saved);
                                  if (booking.slotKey === slotKey) {
                                    if (booking.quantities) nextQuantities = booking.quantities;
                                    if (booking.selectedSeats) nextSeats = booking.selectedSeats;
                                  } else {
                                    sessionStorage.removeItem(`bh-booking-${locationGid}`);
                                  }
                                }
                              } catch (e) { /* ignore */ }
                              
                              setQuantities(nextQuantities);
                              setSelectedSeats(nextSeats);
                              resetTotals();
                              setSelectedSlot(slot);
                            }}
                          >
                            <span className="bh-next-slot-card-date" style={{ color: isActive ? primaryColor : '#101828' }}>
                              {selectedDate.format('MMM DD, YYYY')}
                            </span>
                            <span className="bh-next-slot-card-time" style={{ color: isActive ? primaryColor : '#6A7282' }}>
                              {slot.startTimeLabel} &ndash; {slot.endTimeLabel}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {slots.length > 3 && (
                      <button 
                        onClick={() => scrollSlots('right')}
                        className="bh-next-slots-scroll-btn"
                        style={{
                          border: '1.5px solid #D1D5DC',
                          background: '#fff',
                          borderRadius: '50%',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          flexShrink: 0,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          padding: 0
                        }}
                      >
                        <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
                          <path d="M1 9L5 5L1 1" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Custom Information Form */}
              {customInfoValues.length > 0 && (
                <div className="bh-next-section">
                  <div className="bh-next-section-title">Custom Information</div>
                  <div className="bh-next-custom-fields">
                    {customInfoValues.map((customInfo, index) => (
                      <div key={index} className="bh-next-field-group">
                        <label className="bh-next-field-label">
                          {customInfo.inputFieldLabel} {customInfo.isOptional === false && <span className="bh-next-required">*</span>}
                        </label>
                        {customInfo.inputFieldLabel.toLowerCase().includes("phone") || customInfo.inputFieldType === "INPUT_PHONE" ? (
                          <div className="bh-next-phone-input-wrapper">
                            <Select
                              options={COUNTRIES_LIST.map((c) => ({
                                value: c.dial_code,
                                label: `${c.dial_code}`,
                              }))}
                              value={{
                                value: countryCode,
                                label: countryCode,
                              }}
                              onChange={(val) => setCountryCode(val.value)}
                              styles={{
                                container: (base) => ({
                                  ...base,
                                  width: "120px",
                                }),
                                control: (base) => ({
                                  ...base,
                                  height: "44px",
                                  borderRadius: "8px",
                                  borderColor: "#D1D5DC",
                                  fontSize: "14px",
                                  backgroundColor: "#fff",
                                  boxShadow: "none",
                                  "&:hover": {
                                    borderColor: "#A3A3A3",
                                  },
                                }),
                                valueContainer: (base) => ({
                                  ...base,
                                  padding: "0 10px",
                                }),
                                indicatorsContainer: (base) => ({
                                  ...base,
                                  height: "44px",
                                }),
                              }}
                              placeholder="Code"
                              isSearchable={true}
                              components={{
                                IndicatorSeparator: () => null,
                              }}
                            />
                            <input
                              type="tel"
                              className="bh-next-phone-input"
                              placeholder={`Enter your ${customInfo.inputFieldLabel.toLowerCase()}`}
                              value={customInfo.value}
                              onChange={e => handleInputChange(index, e.target.value)}
                              style={{ flex: 1 }}
                            />
                          </div>
                        ) : (
                          <input
                            type={customInfo.inputFieldType === "INPUT_EMAIL" ? "email" : "text"}
                            className="bh-next-input"
                            placeholder={`Enter your ${customInfo.inputFieldLabel.toLowerCase()}`}
                            value={customInfo.value}
                            onChange={e => handleInputChange(index, e.target.value)}
                          />
                        )}
                        {customInfo && customInfo.isOptional === false && !customInfo.value.trim() && (
                          <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                            This field is required.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ticket Section — exact Figma design */}
              {selectedSlot && (
                <div className="bh-next-section bh-next-ticket-section-figma">

                  {/* Tab Bar (Figma-accurate, matching Event Page design) */}
                  {products.length > 0 && (
                    <div className="bh-next-figma-tab-bar">
                      {products.map(p => {
                        const isActive = activeTab === p.gid;
                        const tabLabel = p.productName || p.name || 'Details';
                        return (
                          <button
                            key={p.gid}
                            className={'bh-next-figma-tab' + (isActive ? ' bh-next-figma-tab--active' : '')}
                            style={isActive ? {
                              color: primaryColor,
                              borderBottomColor: primaryColor,
                              background: `${primaryColor}10`
                            } : {}}
                            onClick={() => setActiveTab(p.gid)}
                          >
                            {tabLabel}
                            {isActive && (
                              <span className="bh-next-figma-tab-indicator" style={{ backgroundColor: primaryColor }} />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Ticket List */}
                  {ticketTypesLoading ? (
                    <div className="bh-next-loading-row">
                      <div className="bh-next-spinner" style={{ borderTopColor: primaryColor }} />
                      <span>Loading items&hellip;</span>
                    </div>
                  ) : products.length === 0 ? (
                    <p className="bh-next-empty">No items available for this slot.</p>
                  ) : (
                    <div className="bh-next-figma-tickets-list">
                      {(() => {
                        const activeProduct = products.find(p => p.gid === activeTab);
                        const currentItems = activeProduct
                          ? (activeProduct.items || []).filter(item => item.isAvailable !== false)
                          : [];
                        if (currentItems.length === 0) {
                          return <p className="bh-next-empty">No items available in this tab.</p>;
                        }
                        return currentItems.map(item => {
                          const qty = quantities[item.gid] || 0;
                          return (
                            <div key={item.gid} className="bh-next-figma-ticket-row">

                              {/* Left: ticket info */}
                              <div className="bh-next-figma-ticket-info">
                                <p className="bh-next-figma-ticket-name">{item.name}</p>
                                {item.description && (
                                  <p className="bh-next-figma-ticket-desc">{item.description}</p>
                                )}
                                <p className="bh-next-figma-ticket-price">{formatAmount(item.amount)}</p>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                  {item.stockEnabled && (
                                    <span style={{ fontSize: '10px', background: item.currentStock > 0 ? '#FEF2F2' : '#F3F4F6', color: item.currentStock > 0 ? '#DC2626' : '#9CA3AF', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold', border: '1px solid', borderColor: item.currentStock > 0 ? '#FCA5A5' : '#E5E7EB' }}>
                                      {item.currentStock > 0 ? `Only ${item.currentStock} left` : 'Sold Out'}
                                    </span>
                                  )}
                                  {item.minQuantity > 0 && !(item.stockEnabled && item.currentStock < item.minQuantity) && (
                                    <span style={{ fontSize: '10px', background: '#F3F4F6', color: '#374151', padding: '2px 8px', borderRadius: '4px', fontWeight: '600', border: '1px solid #E5E7EB' }}>
                                      MIN: {item.minQuantity}
                                    </span>
                                  )}
                                  {item.maxQuantity > 0 && (
                                    <span style={{ fontSize: '10px', background: '#F3F4F6', color: '#374151', padding: '2px 8px', borderRadius: '4px', fontWeight: '600', border: '1px solid #E5E7EB' }}>
                                      MAX: {item.maxQuantity}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Right: stepper or Add Item */}
                              <div className="bh-next-figma-ticket-action">
                                {item.stockEnabled && item.currentStock === 0 ? (
                                  <button
                                    className="bh-next-figma-add-item"
                                    style={{ backgroundColor: '#D1D5DC', cursor: 'not-allowed' }}
                                    disabled
                                  >
                                    Sold Out
                                  </button>
                                ) : qty > 0 ? (
                                  /* Stepper: [-] qty [+] */
                                  <div className="bh-next-figma-stepper">
                                    <button
                                      className="bh-next-figma-stepper-minus"
                                      style={{ borderColor: primaryColor, color: primaryColor }}
                                      onClick={() => handleQtyChange(item.gid, -1)}
                                    >
                                      &#8722;
                                    </button>
                                    <span className="bh-next-figma-stepper-count">{qty}</span>
                                    <button
                                      className="bh-next-figma-stepper-plus"
                                      style={{
                                        backgroundColor: primaryColor,
                                        borderColor: primaryColor,
                                        opacity: ((item.stockEnabled && qty >= item.currentStock) || (item.maxQuantity > 0 && qty >= item.maxQuantity)) ? 0.5 : 1,
                                        cursor: ((item.stockEnabled && qty >= item.currentStock) || (item.maxQuantity > 0 && qty >= item.maxQuantity)) ? 'not-allowed' : 'pointer'
                                      }}
                                      disabled={
                                        (item.stockEnabled && qty >= item.currentStock) ||
                                        (item.maxQuantity > 0 && qty >= item.maxQuantity)
                                      }
                                      onClick={() => handleQtyChange(item.gid, +1)}
                                    >
                                      +
                                    </button>
                                  </div>
                                ) : (
                                  /* Add Item button */
                                  <button
                                    className="bh-next-figma-add-item"
                                    style={{ backgroundColor: primaryColor }}
                                    onClick={() => handleQtyChange(item.gid, 1)}
                                  >
                                    Add Item
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── RIGHT COLUMN: Booking Summary ── */}
        <div className="bh-next-summary-card">
          <div className="bh-next-summary-header" style={{ borderBottomColor: primaryColor + '18' }}>
            <p className="bh-next-summary-title">Booking Summary</p>
          </div>

          <div className="bh-next-summary-body">
            {/* Date & Venue */}
            <div className="bh-next-summary-meta">
              <div className="bh-next-summary-meta-row">
                <span className="bh-next-summary-meta-icon" style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginTop: '2px' }}>
                    <rect x="2" y="3" width="12" height="11" rx="2" stroke="#6A7282" strokeWidth="1.5"/>
                    <path d="M2 7H14M5 2V4M11 2V4" stroke="#6A7282" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </span>
                <div>
                  <p className="bh-next-summary-meta-label">Date &amp; Time</p>
                  <p className="bh-next-summary-meta-val">
                    {selectedDate.format('D MMM YYYY')}
                    {selectedSlot && (` @ ${selectedSlot.startTimeLabel} \u2013 ${selectedSlot.endTimeLabel}`)}
                  </p>
                </div>
              </div>
              {location && (
                <div className="bh-next-summary-meta-row">
                  <span className="bh-next-summary-meta-icon" style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginTop: '2px' }}>
                      <path d="M8 14S3 9.5 3 5.5A5 5 0 0 1 13 5.5C13 9.5 8 14 8 14Z" stroke="#6A7282" strokeWidth="1.5" strokeLinejoin="round"/>
                      <circle cx="8" cy="5.5" r="1.5" stroke="#6A7282" strokeWidth="1.5"/>
                    </svg>
                  </span>
                  <div>
                    <p className="bh-next-summary-meta-label">Venue</p>
                    <p className="bh-next-summary-meta-val" style={{ fontWeight: '700' }}>
                      {location.landmark && `${location.landmark.toUpperCase()} - `}{location.name?.toUpperCase()}
                    </p>
                    <p className="bh-next-summary-meta-val" style={{ fontSize: '11px', color: '#6A7282' }}>
                      {formatAddress(location.address)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Selected Ticket Lines */}
            <div className="bh-next-summary-tickets">
              <p className="bh-next-summary-section-lbl" style={{ color: primaryColor }}>Tickets</p>
              
              {/* Regular Tickets */}
              {ticketTypes.filter(tt => (quantities[tt.gid] || 0) > 0).map(tt => (
                <div key={tt.gid} className="bh-next-summary-line">
                  <span className="bh-next-summary-ticket-line-name">
                    {tt.name}
                    <span className="bh-next-summary-qty">x{quantities[tt.gid]}</span>
                  </span>
                  <span className="bh-next-summary-ticket-line-price">
                    {tt.amount === 0 ? 'Free' : formatAmountNum(tt.amount * quantities[tt.gid])}
                  </span>
                </div>
              ))}

              {/* Seated Tickets */}
              {selectedSeats.map(seat => (
                <div key={`${seat.eventGid}-${seat.id}`} className="bh-next-summary-line">
                  <span className="bh-next-summary-ticket-line-name">
                    {seat.eventName} - {seat.label}
                    <span className="bh-next-summary-qty">x1</span>
                  </span>
                  <span className="bh-next-summary-ticket-line-price">
                    {seat.price === 0 ? 'Free' : formatAmountNum(seat.price)}
                  </span>
                </div>
              ))}

              {/* Empty state fallback */}
              {ticketTypes.filter(tt => (quantities[tt.gid] || 0) > 0).length === 0 && selectedSeats.length === 0 && (
                <p style={{ fontSize: '13px', color: '#888', margin: '8px 0 0 0'}}>
                  No items selected
                </p>
              )}
            </div>

            {/* Promo Code */}
            {location?.acceptPromo === true && (
              <div className="bh-next-summary-promo-section">
                <p className="bh-next-summary-section-lbl" style={{ color: primaryColor }}>Promo Code</p>
                <div className="bh-next-summary-promo-input-group">
                  <input
                    type="text"
                    className="bh-next-summary-promo-input"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={e => setPromoCode(e.target.value)}
                  />
                  <button className="bh-next-summary-promo-btn" style={{ backgroundColor: primaryColor }}>
                    Apply
                  </button>
                </div>
              </div>
            )}

            {/* Add Tip */}
            {location?.acceptTip === true && (
              <div className="bh-next-summary-tip-section">
                <p className="bh-next-summary-section-lbl" style={{ color: primaryColor }}>
                  {location?.tipLabel || 'Add Tip'}
                </p>
                <div className="bh-next-summary-tip-buttons" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <button
                    className={`bh-next-summary-tip-btn${(!selectedTip && !customTipActive) ? ' active' : ''}`}
                    style={(!selectedTip && !customTipActive)
                      ? { borderColor: primaryColor, color: primaryColor, backgroundColor: `${primaryColor}12` }
                      : {}}
                    onClick={() => {
                      setSelectedTip(null);
                      setCustomTipActive(false);
                      setCustomTip('');
                    }}
                  >
                    None
                  </button>

                  {(location?.tips && location.tips.length > 0
                    ? location.tips
                    : [
                        { tip: 500, tipType: 'PERCENTAGE' },
                        { tip: 1000, tipType: 'PERCENTAGE' }
                      ]
                  ).map((t, idx) => {
                    const isSelected = !customTipActive && selectedTip && selectedTip.tip === t.tip && selectedTip.tipType === t.tipType;
                    const label = t.tipType === 'PERCENTAGE' ? `${t.tip / 100}%` : `${currencySymbol} ${(t.tip / 100).toFixed(2)}`;
                    return (
                      <button
                        key={idx}
                        className={`bh-next-summary-tip-btn${isSelected ? ' active' : ''}`}
                        style={isSelected
                          ? { borderColor: primaryColor, color: primaryColor, backgroundColor: `${primaryColor}12` }
                          : {}}
                        onClick={() => {
                          setSelectedTip(t);
                          setCustomTipActive(false);
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}

                  <button
                    className={`bh-next-summary-tip-btn${customTipActive ? ' active' : ''}`}
                    style={customTipActive
                      ? { borderColor: primaryColor, color: primaryColor, backgroundColor: `${primaryColor}12` }
                      : {}}
                    onClick={() => {
                      setSelectedTip(null);
                      setCustomTipActive(true);
                    }}
                  >
                    Custom
                  </button>
                </div>

                {customTipActive && (
                  <div className="bh-next-summary-tip-custom-input-wrapper">
                    <span className="bh-next-summary-tip-currency-symbol">{currencySymbol}</span>
                    <input
                      type="number"
                      className="bh-next-summary-tip-custom-input"
                      placeholder="Enter custom tip"
                      value={customTip}
                      onChange={e => setCustomTip(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Totals */}
            <div className="bh-next-summary-totals">
              <div className="bh-next-summary-line">
                <span>Subtotal</span>
                <span>{formatAmountNum(subTotal)}</span>
              </div>
              {tax > 0 && (
                <div className="bh-next-summary-line">
                  <span>Taxes</span>
                  <span>{formatAmountNum(tax)}</span>
                </div>
              )}
              {serviceFee > 0 && (
                <div className="bh-next-summary-line">
                  <span>Service Fee</span>
                  <span>{formatAmountNum(serviceFee)}</span>
                </div>
              )}
              {getTipAmount() > 0 && (
                <div className="bh-next-summary-line">
                  <span>Tip</span>
                  <span>{formatAmountNum(getTipAmount())}</span>
                </div>
              )}
              <div className="bh-next-summary-total-row" style={{ borderTopColor: primaryColor + '30' }}>
                <span className="bh-next-summary-total-lbl">Total Amount:</span>
                <span className="bh-next-summary-total-val" style={{ color: primaryColor, opacity: splitLoading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                  {formatAmountNum(grandTotal)}
                </span>
              </div>
            </div>

             {/* CTA */}
            <button
              className="bh-next-proceed-btn"
              style={{
                background: (canProceed && !isCreatingOrder) ? primaryColor : '#D1D5DC',
                boxShadow: (canProceed && !isCreatingOrder) ? `0 4px 14px ${primaryColor}40` : 'none',
                cursor: (canProceed && !isCreatingOrder) ? 'pointer' : 'not-allowed',
              }}
              disabled={!canProceed || isCreatingOrder}
              onClick={() => {
                if (events.length > 0 && !showSeats) {
                  setShowSeats(true);
                } else {
                  handleProceed();
                }
              }}
            >
              {isCreatingOrder ? (
                <>
                  <span className="bh-next-spinner" style={{ width: '16px', height: '16px', borderTopColor: '#fff', marginRight: '8px', display: 'inline-block', verticalAlign: 'middle' }} />
                  Creating Booking...
                </>
              ) : (events.length > 0 && !showSeats) ? 'Next' : 'Proceed to Checkout'}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Footer */}
      <div className="bh-next-mobile-footer" style={{ borderTopColor: primaryColor + '20' }}>
        <div className="bh-next-mobile-total">
          <span className="bh-next-mobile-total-lbl">Total</span>
          <span className="bh-next-mobile-total-val" style={{ color: primaryColor, opacity: splitLoading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
            {grandTotal > 0 ? formatAmountNum(grandTotal) : `${currencySymbol}0.00`}
          </span>
        </div>
        <button
          className="bh-next-mobile-cta"
          style={{
            background: (canProceed && !isCreatingOrder) ? primaryColor : '#D1D5DC',
            cursor: (canProceed && !isCreatingOrder) ? 'pointer' : 'not-allowed',
          }}
          disabled={!canProceed || isCreatingOrder}
          onClick={() => {
            if (events.length > 0 && !showSeats) {
              setShowSeats(true);
            } else {
              handleProceed();
            }
          }}
        >
          {isCreatingOrder ? '...' : (events.length > 0 && !showSeats) ? 'NEXT \u2192' : 'PROCEED \u2192'}
        </button>
      </div>

      {showSupportPopup && (
        <div 
          className="bh-next-popup-overlay"
          onClick={() => setShowSupportPopup(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
            animation: 'fadeIn 0.2s ease-out'
          }}
        >
          <div 
            className="bh-next-popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '24px',
              width: '90%',
              maxWidth: '380px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
              transform: 'scale(1)',
              transition: 'transform 0.2s'
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#101828' }}>Contact Support</h3>
              <button 
                onClick={() => setShowSupportPopup(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  color: '#667085',
                  cursor: 'pointer',
                  padding: '4px',
                  lineHeight: 1
                }}
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Name */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '11px', color: '#667085', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</span>
                <span style={{ fontSize: '14px', color: '#101828', fontWeight: '600' }}>
                  {supportContact?.name || location?.name || 'Support Team'}
                </span>
              </div>

              {/* Email */}
              {supportEmail && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '11px', color: '#667085', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</span>
                  <a 
                    href={`mailto:${supportEmail}`}
                    style={{ fontSize: '14px', color: primaryColor, fontWeight: '500', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    {supportEmail}
                  </a>
                </div>
              )}

              {/* Phone */}
              {supportPhone && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '11px', color: '#667085', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phone</span>
                  <a 
                    href={`tel:${supportPhone}`}
                    style={{ fontSize: '14px', color: primaryColor, fontWeight: '500', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    {supportPhone}
                  </a>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowSupportPopup(false)}
                style={{
                  background: primaryColor,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingHubNext;
